import { ContextFormFieldNumber } from '@bambi/component-library/src/components/forms/context/ContextFormFieldNumber';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { MileageRangeSelectorAlert } from './MileageRangeSelectorAlert';

type MileageRangeSelectorProps = {
  lowerFieldPath: string;
  upperFieldPath: string;
};

export function MileageRangeSelector({
  lowerFieldPath,
  upperFieldPath,
}: MileageRangeSelectorProps) {
  const { watch, clearErrors, setValue, setError, getValues } =
    useFormContext();

  const lowerMiles = watch(lowerFieldPath);
  const upperMiles = watch(upperFieldPath);

  const [isRestricted, setIsRestricted] = useState(() => {
    return !!lowerMiles || !!upperMiles;
  });

  const handleRestrict = () => {
    setIsRestricted(true);
    setValue(lowerFieldPath, null);
    setValue(upperFieldPath, null);
  };

  const handleRemove = () => {
    setIsRestricted(false);
    setValue(lowerFieldPath, null);
    setValue(upperFieldPath, null);
    clearErrors([lowerFieldPath, upperFieldPath]);
  };

  if (!isRestricted) {
    return (
      <div className="flex items-center gap-3 rounded-md border border-teal-200 px-4 py-3">
        <span className="text-sm font-semibold text-gray-900">
          Mileage Range
        </span>
        <span className="rounded-full bg-teal-100 px-2 py-0.5 text-xs font-medium text-teal-700">
          All mileages included
        </span>
        <button
          type="button"
          className="ml-auto text-sm font-medium text-teal-600 underline"
          onClick={handleRestrict}
        >
          Restrict to mileage range
        </button>
      </div>
    );
  }

  return (
    <div className="rounded-md border border-yellow-300 bg-yellow-50 p-4">
      <div className="mb-3 flex items-center justify-between">
        <span className="text-sm font-semibold text-gray-900">
          Mileage Range
        </span>
        <button
          type="button"
          className="text-sm font-medium text-red-500 underline"
          onClick={handleRemove}
        >
          Remove mileage restriction
        </button>
      </div>
      <div className="flex gap-3">
        <div className="flex-1">
          <ContextFormFieldNumber
            fieldPath={lowerFieldPath}
            label="Minimum Miles"
            placeholder="0"
            rules={{
              validate: (value) => {
                const upper = getValues(upperFieldPath);
                if (value && upper && Number(value) >= Number(upper)) {
                  setError(upperFieldPath, {
                    message: 'Maximum miles must be greater than minimum miles',
                  });
                } else {
                  clearErrors(upperFieldPath);
                }
                return true;
              },
            }}
          />
        </div>
        <div className="flex-1">
          <ContextFormFieldNumber
            fieldPath={upperFieldPath}
            label="Maximum Miles"
            helperText="Leave blank for no upper limit"
            rules={{
              validate: (value) => {
                const lower = getValues(lowerFieldPath);
                if (value && lower && Number(value) <= Number(lower)) {
                  return 'Maximum miles must be greater than minimum miles';
                }
                return true;
              },
            }}
          />
        </div>
      </div>
      <MileageRangeSelectorAlert
        lowerMiles={lowerMiles}
        upperMiles={upperMiles}
      />
    </div>
  );
}
